import { useState } from 'react';
import {
  Search,
  ArrowDownCircle,
  ArrowUpCircle,
  Building2,
  Link2,
  Loader2,
  EyeOff,
  Sparkles,
  CheckCircle2,
  FileUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useBankTransactions, BankTransaction } from '@/hooks/useBankTransactions';
import { useSystemPayments, SystemPayment } from '@/hooks/useSystemPayments';
import { findBestMatch } from '@/utils/reconciliationMatcher';
import { BankTransactionCard } from './BankTransactionCard';
import { SystemTransactionCard } from './SystemTransactionCard';
import { CreateTransactionFromBankDialog } from '@/components/Financial/Reconciliation/CreateTransactionFromBankDialog';
import { SearchReceivablesDialog } from '@/components/Financial/Reconciliation/SearchReceivablesDialog';
import { ReviewValuesDialog, ReceivableAdjustment } from '@/components/Financial/Reconciliation/ReviewValuesDialog';
import { CreateTransferDialog } from '@/components/Financial/Reconciliation/CreateTransferDialog';
import { SearchableReceivable } from '@/hooks/useSearchReceivables';
import { useInternalTransfer } from '@/hooks/useInternalTransfer';
import { supabase } from '@/integrations/supabase/client';
import { useReceivableMutations } from '@/hooks/useReceivableMutations';
import { useAccountsPayable } from '@/hooks/useAccountsPayable';

interface AccountReconciliationPanelProps {
  accountId: string;
  startDate: string;
  endDate: string;
  onRefresh?: () => void;
}

export const AccountReconciliationPanel = ({
  accountId,
  startDate,
  endDate,
  onRefresh,
}: AccountReconciliationPanelProps) => {
  const { toast } = useToast();
  const internalTransfer = useInternalTransfer();
  const { recordPaymentAsync: recordReceivablePaymentAsync } = useReceivableMutations();
  const { recordPaymentAsync: recordPayablePaymentAsync } = useAccountsPayable({});
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'credit' | 'debit'>('all');
  const [showIgnored, setShowIgnored] = useState(false);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  
  // Selected data
  const [selectedTransaction, setSelectedTransaction] = useState<BankTransaction | null>(null);
  const [selectedReceivables, setSelectedReceivables] = useState<SearchableReceivable[]>([]);

  const {
    transactions: bankTransactions,
    isLoading: isLoadingBank,
    pendingCount,
    reconcile,
    ignore,
    unlink,
    refetch: refetchBank,
  } = useBankTransactions({
    financialAccountId: accountId,
    status: showIgnored ? 'ignored' : 'pending',
    startDate,
    endDate,
    transactionType: typeFilter === 'all' ? undefined : typeFilter,
  });

  const { payments: systemPayments, isLoading: isLoadingPayments } = useSystemPayments({
    financialAccountId: accountId,
    startDate,
    endDate,
    unreconciled: true,
  });

  // Filtrar por busca
  const filteredTransactions = bankTransactions.filter(tx => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      tx.description.toLowerCase().includes(search) ||
      tx.counterpart_name?.toLowerCase().includes(search) ||
      tx.value.toString().includes(search)
    );
  });

  // Sugestão automática via scorer partilhado (morada única — D-140C-ii.5)
  const findMatch = (bankTx: BankTransaction): SystemPayment | null => {
    const best = findBestMatch(bankTx, systemPayments.filter(p => !p.reconciled));
    return best && best.score >= 60 ? best.payment : null;
  };

  const creditCount = bankTransactions.filter(t => t.transaction_type === 'credit').length;
  const debitCount = bankTransactions.filter(t => t.transaction_type === 'debit').length;

  const handleReconcile = (tx: BankTransaction, payment: SystemPayment) => {
    reconcile({
      transactionId: tx.id,
      receivablePaymentId: payment.type === 'receivable' ? payment.id : undefined,
      payablePaymentId: payment.type === 'payable' ? payment.id : undefined,
    });
    onRefresh?.();
  };

  const handleIgnore = (tx: BankTransaction, reason: string) => {
    ignore({ transactionId: tx.id, reason });
    onRefresh?.();
  };

  const handleUnlink = (tx: BankTransaction, reason: string) => {
    unlink({ id: tx.id, reason });
    onRefresh?.();
  };

  const handleCreateTransaction = (tx: BankTransaction) => {
    setSelectedTransaction(tx);
    setShowCreateDialog(true);
  };

  const handleSearchReceivables = (tx: BankTransaction) => {
    setSelectedTransaction(tx);
    setShowSearchDialog(true);
  };

  const handleCreateTransfer = (tx: BankTransaction) => {
    setSelectedTransaction(tx);
    setShowTransferDialog(true);
  };

  const handleReviewValues = (receivables: SearchableReceivable[]) => {
    setSelectedReceivables(receivables);
    setShowSearchDialog(false);
    setShowReviewDialog(true);
  };

  // Vínculo puro (R3): localizar payment do título ainda não vinculado
  const findLinkablePaymentId = async (titleId: string, type: 'receivable' | 'payable'): Promise<string | null> => {
    if (type === 'receivable') {
      const { data } = await supabase
        .from('receivable_payments')
        .select('id, bank_transaction_id')
        .eq('receivable_id', titleId)
        .order('created_at', { ascending: true });
      const ids = (data || []).filter((p) => !p.bank_transaction_id).map((p) => p.id);
      if (!ids.length) return null;
      // E1: vínculos legados podem existir só no ponteiro do lado bank
      const { data: linked } = await supabase
        .from('bank_transactions')
        .select('receivable_payment_id')
        .in('receivable_payment_id', ids);
      const linkedSet = new Set((linked || []).map((l) => l.receivable_payment_id));
      return ids.find((id) => !linkedSet.has(id)) ?? null;
    }
    const { data } = await supabase
      .from('payable_payments')
      .select('id')
      .eq('payable_id', titleId)
      .order('created_at', { ascending: true });
    const ids = (data || []).map((p) => p.id);
    if (!ids.length) return null;
    // Anti-duplo-vínculo: payable não tem bank_transaction_id —
    // excluir payments já apontados pelo outro lado (bank_transactions.payable_payment_id)
    const { data: linked } = await supabase
      .from('bank_transactions')
      .select('payable_payment_id')
      .in('payable_payment_id', ids);
    const linkedSet = new Set((linked || []).map((l) => l.payable_payment_id));
    return ids.find((id) => !linkedSet.has(id)) ?? null;
  };

  // Gravação por cenário: pago → vínculo puro (NUNCA re-baixa); aberto → baixa decomposta + vínculo
  const reconcileScenario = async (
    tx: BankTransaction,
    adj: {
      id: string;
      type: 'receivable' | 'payable';
      settled: boolean;
      original_value: number;
      interest_value: number;
      penalty_value: number;
      discount_value: number;
      fee_value: number;
      partial_payment: number;
    }
  ) => {
    if (adj.settled) {
      const paymentId = await findLinkablePaymentId(adj.id, adj.type);
      if (!paymentId) {
        toast({ title: 'Título sem pagamento disponível para vínculo', variant: 'destructive' });
        return false;
      }
      reconcile({
        transactionId: tx.id,
        receivablePaymentId: adj.type === 'receivable' ? paymentId : undefined,
        payablePaymentId: adj.type === 'payable' ? paymentId : undefined,
      });
      return true;
    }
    const base = {
      paymentDate: tx.transaction_date,
      // E2: baixa parcial — grava o líquido de partial_payment (o gate fecha sobre ele)
      paymentValue: adj.original_value - (adj.partial_payment || 0),
      paymentMethod: 'bank_transfer',
      financialAccountId: tx.financial_account_id,
      interestValue: adj.interest_value,
      fineValue: adj.penalty_value,
      discountValue: adj.discount_value,
      feeValue: adj.fee_value,
    };
    const res = adj.type === 'receivable'
      ? await recordReceivablePaymentAsync({ receivableId: adj.id, ...base })
      : await recordPayablePaymentAsync({ payableId: adj.id, ...base });
    if (!res?.paymentId) {
      toast({ title: 'Baixa registrada, mas vínculo não concluído (payment não identificado)', variant: 'destructive' });
      return false;
    }
    reconcile({
      transactionId: tx.id,
      receivablePaymentId: adj.type === 'receivable' ? res.paymentId : undefined,
      payablePaymentId: adj.type === 'payable' ? res.paymentId : undefined,
    });
    return true;
  };

  const handleDirectReconcile = async (receivables: SearchableReceivable[]) => {
    if (!selectedTransaction || receivables.length === 0) return;

    // Limitação declarada: processa o primeiro selecionado (N:1 fino = refinamento futuro)
    const r = receivables[0];
    const remaining = r.total_value - (r.paid_value || 0);
    const ok = await reconcileScenario(selectedTransaction, {
      id: r.id,
      type: r.type,
      settled: r.status === 'paid' || remaining <= 0.009,
      original_value: remaining,
      interest_value: 0,
      penalty_value: 0,
      discount_value: 0,
      fee_value: 0,
      partial_payment: 0,
    });
    if (!ok) return;

    setShowSearchDialog(false);
    setSelectedTransaction(null);
    onRefresh?.();
  };

  const handleConfirmWithAdjustments = async (adjustments: ReceivableAdjustment[]) => {
    if (!selectedTransaction || adjustments.length === 0) return;

    // Limitação declarada: primeiro lançamento (N:1 fino = refinamento futuro)
    const ok = await reconcileScenario(selectedTransaction, adjustments[0]);
    if (!ok) return;

    setShowReviewDialog(false);
    setSelectedTransaction(null);
    setSelectedReceivables([]);
    onRefresh?.();
  };

  const handleConfirmTransfer = async (selectedAccountId: string) => {
    if (!selectedTransaction) return;

    // Lado adotado pela RPC = perna correspondente ao tipo da linha do banco:
    // credit = entrada na conta atual (atual é destino); debit = saída (atual é origem)
    const isCredit = selectedTransaction.transaction_type === 'credit';

    try {
      await internalTransfer.mutateAsync({
        originAccountId: isCredit ? selectedAccountId : accountId,
        destinationAccountId: isCredit ? accountId : selectedAccountId,
        amount: Math.abs(selectedTransaction.value),
        exitDate: selectedTransaction.transaction_date,
        entryDate: isCredit ? selectedTransaction.transaction_date : undefined,
        description: selectedTransaction.description,
        bankTransactionId: selectedTransaction.id,
      });
      setShowTransferDialog(false);
      setSelectedTransaction(null);
      onRefresh?.();
    } catch {
      // Erro tratado pelo onError do hook
    }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Pesquisar por descrição ou valor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant={showIgnored ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setShowIgnored(!showIgnored)}
          >
            <EyeOff className="h-4 w-4 mr-2" />
            Ver ignorados
          </Button>
        </div>
      </div>

      {/* Type Filters */}
      <div className="flex items-center gap-4">
        <Tabs value={typeFilter} onValueChange={(v) => setTypeFilter(v as any)}>
          <TabsList>
            <TabsTrigger value="all" className="gap-2">
              Todos
              <Badge variant="secondary" className="rounded-full h-5 min-w-5">
                {bankTransactions.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="credit" className="gap-2">
              <ArrowDownCircle className="h-4 w-4 text-green-600" />
              Recebimentos
              <Badge variant="secondary" className="rounded-full h-5 min-w-5">
                {creditCount}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="debit" className="gap-2">
              <ArrowUpCircle className="h-4 w-4 text-red-600" />
              Pagamentos
              <Badge variant="secondary" className="rounded-full h-5 min-w-5">
                {debitCount}
              </Badge>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Side by Side Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Column - Bank Transactions */}
        <Card className="min-w-0">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Lançamentos do Banco
              <Badge variant="secondary" className="ml-auto">
                {filteredTransactions.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              {isLoadingBank ? (
                <div className="p-8 text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                </div>
              ) : filteredTransactions.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  {showIgnored ? (
                    <>
                      <EyeOff className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>Nenhum lançamento ignorado</p>
                    </>
                  ) : searchTerm ? (
                    <>
                      <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>Nenhum resultado para "{searchTerm}"</p>
                    </>
                  ) : pendingCount === 0 && bankTransactions.length === 0 ? (
                    <>
                      <FileUp className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>Nenhum extrato importado</p>
                      <p className="text-sm mt-1">Faça o upload de um arquivo OFX para iniciar a conciliação.</p>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-8 w-8 mx-auto mb-2 opacity-50 text-green-600" />
                      <p>Todas as transações foram conciliadas!</p>
                    </>
                  )}
                </div>
              ) : (
                <div className="divide-y">
                  {filteredTransactions.map(tx => {
                    const suggestedMatch = findMatch(tx);
                    return (
                        <BankTransactionCard
                        key={tx.id}
                        transaction={tx}
                        suggestedMatch={suggestedMatch}
                        onReconcile={() => suggestedMatch && handleReconcile(tx, suggestedMatch)}
                        onIgnore={(reason) => handleIgnore(tx, reason)}
                        onUnlink={(reason) => handleUnlink(tx, reason)}
                        onCreateTransaction={() => handleCreateTransaction(tx)}
                        onSearchReceivables={() => handleSearchReceivables(tx)}
                        onCreateTransfer={() => handleCreateTransfer(tx)}
                        isIgnored={showIgnored}
                      />
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Right Column - System Payments */}
        <Card className="min-w-0">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Link2 className="h-4 w-4" />
              Lançamentos do Sistema
              <Badge variant="secondary" className="ml-auto">
                {systemPayments.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              {isLoadingPayments ? (
                <div className="p-8 text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                </div>
              ) : systemPayments.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Link2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Nenhum pagamento pendente</p>
                  <p className="text-sm">Todos os pagamentos já foram conciliados</p>
                </div>
              ) : (
                <div className="divide-y">
                  {systemPayments.map(payment => (
                    <SystemTransactionCard key={payment.id} payment={payment} />
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Dialogs */}
      <CreateTransactionFromBankDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        transaction={selectedTransaction}
        onSuccess={() => {
          refetchBank();
          onRefresh?.();
        }}
      />

      <SearchReceivablesDialog
        open={showSearchDialog}
        onOpenChange={setShowSearchDialog}
        transaction={selectedTransaction}
        onReconcile={handleDirectReconcile}
        onReviewValues={handleReviewValues}
      />

      <ReviewValuesDialog
        open={showReviewDialog}
        onOpenChange={setShowReviewDialog}
        transaction={selectedTransaction}
        receivables={selectedReceivables}
        onConfirm={handleConfirmWithAdjustments}
        onBack={() => {
          setShowReviewDialog(false);
          setShowSearchDialog(true);
        }}
      />

      <CreateTransferDialog
        open={showTransferDialog}
        onOpenChange={setShowTransferDialog}
        transaction={selectedTransaction}
        currentAccountId={accountId}
        onConfirm={handleConfirmTransfer}
        isLoading={internalTransfer.isPending}
      />
    </div>
  );
};
